import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Scale, BookOpen, FileText, FileCheck, DollarSign,
  Shield, CheckSquare, Copyright, Store, ArrowRight, Briefcase
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const ServicesOverview = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const services = [
    {
      id: 1,
      icon: Scale,
      nameAr: 'دائرة التقاضي',
      nameEn: 'Litigation Department',
      descAr: 'تمثيل قانوني متميز أمام جميع الجهات القضائية',
      descEn: 'Distinguished legal representation before all judicial authorities',
      path: '/services/litigation',
      color: 'from-wazin-navy to-wazin-navy-600',
      servicesCount: 9
    },
    {
      id: 2,
      icon: BookOpen,
      nameAr: 'دائرة الدراسات والاستشارات',
      nameEn: 'Studies and Consultation',
      descAr: 'استشارات قانونية متخصصة ودراسات قانونية معمقة',
      descEn: 'Specialized legal consultations and in-depth legal studies',
      path: '/services/consultation',
      color: 'from-wazin-gold to-wazin-gold-600',
      servicesCount: 8
    },
    {
      id: 3,
      icon: FileText,
      nameAr: 'دائرة العقود والشركات',
      nameEn: 'Contracts and Companies',
      descAr: 'صياغة ومراجعة العقود وتأسيس الكيانات القانونية',
      descEn: 'Drafting and reviewing contracts and establishing legal entities',
      path: '/services/contracts',
      color: 'from-wazin-navy-400 to-wazin-navy-600',
      servicesCount: 9
    },
    {
      id: 4,
      icon: FileCheck,
      nameAr: 'دائرة التوثيق',
      nameEn: 'Notarization Department',
      descAr: 'خدمات التوثيق الرسمي والمحررات القانونية',
      descEn: 'Official notarization services and legal documents',
      path: '/services/notarization',
      color: 'from-wazin-gold-500 to-wazin-gold-700',
      servicesCount: 8
    },
    {
      id: 5,
      icon: DollarSign,
      nameAr: 'دائرة التحصيل والتسويات',
      nameEn: 'Collection and Settlements',
      descAr: 'تحصيل المستحقات وحل النزاعات المالية ودياً',
      descEn: 'Debt collection and amicable resolution of financial disputes',
      path: '/services/collection',
      color: 'from-wazin-gold-400 to-wazin-gold-600',
      servicesCount: 8
    },
    {
      id: 6,
      icon: Shield,
      nameAr: 'دائرة الجرائم المعلوماتية',
      nameEn: 'Cybercrime Department',
      descAr: 'حماية قانونية متخصصة في الجرائم الإلكترونية',
      descEn: 'Specialized legal protection in electronic crimes',
      path: '/services/cybercrime',
      color: 'from-wazin-navy-500 to-wazin-navy-700',
      servicesCount: 8
    },
    {
      id: 7,
      icon: CheckSquare,
      nameAr: 'دائرة الامتثال والحوكمة',
      nameEn: 'Compliance and Governance',
      descAr: 'بناء أنظمة الامتثال وتطبيق معايير الحوكمة',
      descEn: 'Building compliance systems and implementing governance standards',
      path: '/services/compliance',
      color: 'from-wazin-gold-600 to-wazin-navy-600',
      servicesCount: 8
    },
    {
      id: 8,
      icon: Copyright,
      nameAr: 'دائرة الملكية الفكرية',
      nameEn: 'Intellectual Property',
      descAr: 'حماية شاملة لحقوق الملكية الفكرية',
      descEn: 'Comprehensive protection of intellectual property rights',
      path: '/services/ip',
      color: 'from-wazin-navy-600 to-wazin-gold-600',
      servicesCount: 8
    },
    {
      id: 9,
      icon: Store,
      nameAr: 'دائرة الامتياز التجاري',
      nameEn: 'Franchise Department',
      descAr: 'حلول قانونية متكاملة للامتياز التجاري',
      descEn: 'Comprehensive legal solutions for commercial franchising',
      path: '/services/franchise',
      color: 'from-wazin-gold to-wazin-gold-700',
      servicesCount: 8
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-wazin-navy via-wazin-navy-700 to-wazin-navy-dark text-white py-20"
        style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}
      >
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-wazin-gold to-wazin-gold-600 rounded-full mb-6">
              <Briefcase className="w-10 h-10 text-wazin-navy" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              {i18n.language === 'ar' ? 'جميع خدماتنا القانونية' : 'All Our Legal Services'}
            </h1>
            
            <p className="text-xl text-wazin-gold-100 mb-8 max-w-2xl mx-auto">
              {i18n.language === 'ar' 
                ? 'نقدم خدمات قانونية شاملة من خلال 9 دوائر متخصصة لتلبية جميع احتياجاتكم القانونية'
                : 'We provide comprehensive legal services through 9 specialized departments to meet all your legal needs'
              }
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <span className="text-wazin-gold font-bold">9</span> {i18n.language === 'ar' ? 'دوائر متخصصة' : 'Specialized Departments'}
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <span className="text-wazin-gold font-bold">70+</span> {i18n.language === 'ar' ? 'خدمة قانونية' : 'Legal Services'}
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <span className="text-wazin-gold font-bold">15+</span> {i18n.language === 'ar' ? 'عام خبرة' : 'Years Experience'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16" style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
        
        {/* Introduction */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {i18n.language === 'ar' ? 'الدوائر القانونية المتخصصة' : 'Specialized Legal Departments'}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {i18n.language === 'ar'
                ? 'في وازن، قمنا بتنظيم خدماتنا القانونية في دوائر متخصصة لضمان تقديم أعلى مستوى من الخبرة والاحترافية. كل دائرة تضم فريقاً من المحامين الخبراء المتخصصين في مجالهم.'
                : 'At Wazin, we have organized our legal services into specialized departments to ensure the highest level of expertise and professionalism. Each department includes a team of expert lawyers specialized in their field.'
              }
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card 
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 cursor-pointer border-t-4 overflow-hidden"
                style={{ borderTopColor: `var(--${service.color.split('-')[1]}-500)` }}
                onClick={() => navigate(service.path)}
              >
                <CardContent className="p-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl mb-4 group-hover:scale-110 transition-transform`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-wazin-gold transition-colors">
                    {i18n.language === 'ar' ? service.nameAr : service.nameEn}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {i18n.language === 'ar' ? service.descAr : service.descEn}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {service.servicesCount}+ {i18n.language === 'ar' ? 'خدمة' : 'services'}
                    </span>
                    <ArrowRight className={`w-5 h-5 text-wazin-gold group-hover:translate-x-1 transition-transform ${i18n.language === 'ar' ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-16 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-12 text-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {i18n.language === 'ar' ? 'لماذا تختار وازن؟' : 'Why Choose Wazin?'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2 text-wazin-gold">
                  {i18n.language === 'ar' ? 'تخصص عميق' : 'Deep Specialization'}
                </h3>
                <p className="text-gray-300">
                  {i18n.language === 'ar'
                    ? 'كل دائرة تضم محامين متخصصين في مجالهم مع خبرة عميقة'
                    : 'Each department includes specialized lawyers with deep expertise'
                  }
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2 text-wazin-gold">
                  {i18n.language === 'ar' ? 'تغطية شاملة' : 'Comprehensive Coverage'}
                </h3>
                <p className="text-gray-300">
                  {i18n.language === 'ar'
                    ? 'نغطي جميع المجالات القانونية تحت سقف واحد'
                    : 'We cover all legal areas under one roof'
                  }
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2 text-wazin-gold">
                  {i18n.language === 'ar' ? 'نتائج مثبتة' : 'Proven Results'}
                </h3>
                <p className="text-gray-300">
                  {i18n.language === 'ar'
                    ? 'معدل نجاح عالٍ في جميع القضايا والخدمات'
                    : 'High success rate in all cases and services'
                  }
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2 text-wazin-gold">
                  {i18n.language === 'ar' ? 'تقنية متقدمة' : 'Advanced Technology'}
                </h3>
                <p className="text-gray-300">
                  {i18n.language === 'ar'
                    ? 'منصة رقمية متطورة مع ذكاء اصطناعي قانوني'
                    : 'Advanced digital platform with legal AI'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-2xl p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {i18n.language === 'ar' 
                ? 'هل أنت مستعد للبدء؟'
                : 'Ready to Get Started?'
              }
            </h2>
            <p className="text-gray-700 mb-8 max-w-2xl mx-auto">
              {i18n.language === 'ar' 
                ? 'احجز استشارة مجانية اليوم واكتشف كيف يمكن لفريقنا القانوني المتخصص مساعدتك'
                : 'Book a free consultation today and discover how our specialized legal team can help you'
              }
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={() => navigate('/book-consultation')}
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-6 text-lg hover:shadow-lg transition-all"
              >
                {i18n.language === 'ar' ? 'احجز استشارة مجانية' : 'Book Free Consultation'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-2 border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-6 text-lg"
              >
                {i18n.language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </Button>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ServicesOverview;
